(function () {
  const parseExpiry = (title) => {
    const timestamp = Date.parse(title);
    return Number.isNaN(timestamp) ? null : timestamp;
  };

  const availableQuantity = (variant, quantitiesInCart) => {
    if (!variant.inventory_management || variant.inventory_policy === 'continue') return Infinity;
    return Math.max(0, Number(variant.inventory_quantity || 0) - (quantitiesInCart.get(variant.id) || 0));
  };

  window.EurusExpiryVariants = {
    getSectionsPayload() {
      const sections = Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id);

      return {
        // Shopify only supports up to five bundled section renders per Cart API request.
        // When the cart page needs more, reRenderSections will fetch them separately.
        ...(sections.length <= 5 ? { sections } : {}),
        sections_url: window.location.pathname
      };
    },

    setGroupLoading(row, loading) {
      row.setAttribute('aria-busy', loading ? 'true' : 'false');
      row.querySelectorAll('button').forEach((button) => {
        button.disabled = loading;
      });
      const loadingElement = row.querySelector('[id^="loading-"]');
      if (loadingElement) loadingElement.classList.toggle('hidden', !loading);
    },

    async renderCartResponse(response) {
      await Alpine.store('xCartHelper').reRenderSections(response.sections || null);
      const cartCount = document.querySelector('#cart-icon-bubble span');
      if (cartCount) Alpine.store('xCartHelper').currentItemCount = parseInt(cartCount.textContent, 10) || 0;
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      requestAnimationFrame(() => window.EurusExpiryVariants.mergeCartRows());
    },

    async updateGroup({ groupId, productUrl, inventoryData, primaryVariantId, desiredQuantity }) {
      const [productResponse, cartResponse] = await Promise.all([
        fetch(`${productUrl.split('?')[0].replace(/\/$/, '')}.js`, { headers: { Accept: 'application/json' } }),
        fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } })
      ]);
      if (!productResponse.ok || !cartResponse.ok) throw new Error('Unable to load cart inventory');

      const [product, cart] = await Promise.all([productResponse.json(), cartResponse.json()]);
      product.variants = product.variants.map((variant) => ({
        ...variant,
        ...(inventoryData[variant.id] || {})
      }));

      const groupItems = cart.items.filter((item) => item.properties?._expiry_group === groupId);
      const primaryVariant = product.variants.find((variant) => Number(variant.id) === Number(primaryVariantId));
      const primaryExpiry = primaryVariant && parseExpiry(primaryVariant.title);
      if (!groupItems.length || !primaryVariant || primaryExpiry === null) throw new Error('Expiry group not found');

      const quantitiesOutsideGroup = new Map();
      cart.items.forEach((item) => {
        if (item.properties?._expiry_group !== groupId) {
          quantitiesOutsideGroup.set(item.variant_id, (quantitiesOutsideGroup.get(item.variant_id) || 0) + item.quantity);
        }
      });

      const candidates = product.variants
        .filter((variant) => {
          const expiry = parseExpiry(variant.title);
          return expiry !== null
            && expiry >= primaryExpiry
            && variant.price === primaryVariant.price
            && variant.compare_at_price === primaryVariant.compare_at_price;
        })
        .sort((a, b) => parseExpiry(a.title) - parseExpiry(b.title));

      let remaining = desiredQuantity;
      const allocations = new Map();
      for (const variant of candidates) {
        const available = !variant.inventory_management || variant.inventory_policy === 'continue'
          ? remaining
          : Math.max(0, Number(variant.inventory_quantity || 0) - (quantitiesOutsideGroup.get(variant.id) || 0));
        const quantity = Math.min(remaining, available);
        if (quantity > 0) allocations.set(Number(variant.id), quantity);
        remaining -= quantity;
        if (remaining === 0) break;
      }
      if (remaining > 0) throw new Error('No additional matching expiry stock is available');

      const updates = {};
      const existingVariantIds = new Set();
      groupItems.forEach((item) => {
        existingVariantIds.add(Number(item.variant_id));
        updates[item.key] = allocations.get(Number(item.variant_id)) || 0;
      });

      const sectionsPayload = this.getSectionsPayload();
      const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, ...sectionsPayload })
      });
      const updatedCart = await updateResponse.json();
      if (!updateResponse.ok || updatedCart.status === 422) throw new Error(updatedCart.description || 'Unable to update the cart');

      const newItems = [...allocations.entries()]
        .filter(([variantId]) => !existingVariantIds.has(variantId))
        .map(([variantId, quantity]) => ({
          id: variantId,
          quantity,
          properties: {
            _expiry_group: groupId,
            _expiry_primary: Number(variantId) === Number(primaryVariantId) ? 'true' : 'false'
          }
        }));

      if (newItems.length) {
        const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: newItems, ...sectionsPayload })
        });
        const addedCart = await addResponse.json();
        if (!addResponse.ok || addedCart.status === 422) throw new Error(addedCart.description || 'Unable to update the cart');
        await this.renderCartResponse(addedCart);
      } else {
        await this.renderCartResponse(updatedCart);
      }
    },

    mergeCartRows(root = document) {
      const groups = new Map();
      root.querySelectorAll('.cart-item[data-expiry-group]').forEach((row) => {
        const surface = row.closest('#CartDrawer') ? 'drawer' : 'page';
        const groupId = `${surface}:${row.dataset.expiryGroup}`;
        if (!groupId) return;
        if (!groups.has(groupId)) groups.set(groupId, []);
        groups.get(groupId).push(row);
      });

      groups.forEach((rows) => {
        const primary = rows.find((row) => row.dataset.expiryPrimary === 'true') || rows[0];
        const quantity = rows.reduce((total, row) => total + Number(row.dataset.expiryQuantity || 0), 0);
        const finalLinePrice = rows.reduce((total, row) => total + Number(row.dataset.expiryFinalLinePrice || 0), 0);
        const keys = rows.map((row) => row.dataset.expiryKey).filter(Boolean);

        rows.forEach((row) => {
          if (row === primary) {
            row.hidden = false;
            row.style.removeProperty('display');
          } else {
            row.hidden = true;
            row.style.setProperty('display', 'none', 'important');
          }
        });

        primary.querySelectorAll('input[type="number"]').forEach((input) => {
          input.value = quantity;
          input.readOnly = true;
        });
        primary.querySelectorAll('button[aria-label="button-minus"], button[aria-label="button-plus"]').forEach((button) => {
          button.hidden = false;
          if (button.dataset.expiryGroupBound) return;
          button.dataset.expiryGroupBound = 'true';
          button.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            const delta = button.getAttribute('aria-label') === 'button-plus' ? 1 : -1;
            const desiredQuantity = Math.max(0, quantity + delta);
            window.EurusExpiryVariants.setGroupLoading(primary, true);
            try {
              await window.EurusExpiryVariants.updateGroup({
                groupId: primary.dataset.expiryGroup,
                productUrl: primary.dataset.expiryProductUrl,
                inventoryData: JSON.parse(primary.dataset.expiryVariantInventory || '{}'),
                primaryVariantId: primary.dataset.expiryVariantId,
                desiredQuantity
              });
            } catch (error) {
              window.EurusExpiryVariants.setGroupLoading(primary, false);
              window.alert(error.message);
            }
          }, true);
        });
        primary.querySelectorAll('[data-expiry-line-total]').forEach((price) => {
          const formattedPrice = new Intl.NumberFormat(document.documentElement.lang || 'en-SG', {
            style: 'currency',
            currency: window.Shopify.currency?.active || 'SGD'
          }).format(finalLinePrice / 100);
          if (price.textContent !== formattedPrice) price.textContent = formattedPrice;
        });

        const removeButton = primary.querySelector('button[aria-label="button-remove"]');
        if (removeButton && !removeButton.dataset.expiryGroupBound) {
          removeButton.dataset.expiryGroupBound = 'true';
          removeButton.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            window.EurusExpiryVariants.setGroupLoading(primary, true);
            const updates = Object.fromEntries(keys.map((key) => [key, 0]));
            try {
              const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ updates, ...window.EurusExpiryVariants.getSectionsPayload() })
              });
              const cart = await response.json();
              if (!response.ok || cart.status === 422) throw new Error(cart.description || 'Unable to remove the item');
              await window.EurusExpiryVariants.renderCartResponse(cart);
            } catch (error) {
              window.EurusExpiryVariants.setGroupLoading(primary, false);
              window.alert(error.message);
            }
          }, true);
        }
      });
    },

    async prepareCartAdd(form, originalFormData) {
      const productUrl = form.dataset.productUrl;
      const inventoryData = JSON.parse(form.dataset.expiryVariantInventory || '{}');
      const currentVariantId = Number(originalFormData.get('id'));
      const requestedQuantity = Number(originalFormData.get('quantity') || 1);

      if (!productUrl || !currentVariantId || !Number.isInteger(requestedQuantity) || requestedQuantity < 1) {
        return originalFormData;
      }

      // Linked products, gift wrapping, and other multi-item features retain the theme's native flow.
      if ([...originalFormData.keys()].some((key) => key.startsWith('items['))) {
        return originalFormData;
      }

      const productJsonUrl = `${productUrl.split('?')[0].replace(/\/$/, '')}.js`;
      const [productResponse, cartResponse] = await Promise.all([
        fetch(productJsonUrl, { headers: { Accept: 'application/json' } }),
        fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } })
      ]);

      if (!productResponse.ok || !cartResponse.ok) return originalFormData;

      const [product, cart] = await Promise.all([productResponse.json(), cartResponse.json()]);
      product.variants = product.variants.map((variant) => ({
        ...variant,
        ...(inventoryData[variant.id] || {})
      }));
      const currentVariant = product.variants.find((variant) => Number(variant.id) === currentVariantId);
      const currentExpiry = currentVariant && parseExpiry(currentVariant.title);

      if (!currentVariant || currentExpiry === null || currentVariant.inventory_policy === 'continue') {
        return originalFormData;
      }

      const quantitiesInCart = new Map();
      cart.items.forEach((item) => {
        quantitiesInCart.set(item.variant_id, (quantitiesInCart.get(item.variant_id) || 0) + item.quantity);
      });

      const matchingVariants = product.variants
        .filter((variant) => {
          const expiry = parseExpiry(variant.title);
          return expiry !== null
            && expiry >= currentExpiry
            && variant.price === currentVariant.price
            && variant.compare_at_price === currentVariant.compare_at_price;
        })
        .sort((a, b) => parseExpiry(a.title) - parseExpiry(b.title));

      const currentIndex = matchingVariants.findIndex((variant) => Number(variant.id) === currentVariantId);
      if (currentIndex === -1) return originalFormData;

      let remaining = requestedQuantity;
      const allocations = [];
      for (const variant of matchingVariants.slice(currentIndex)) {
        const available = availableQuantity(variant, quantitiesInCart);
        const quantity = Math.min(remaining, available);
        if (quantity > 0) allocations.push({ id: variant.id, quantity });
        remaining -= quantity;
        if (remaining === 0) break;
      }

      // Preserve Shopify's normal validation and error message when total matching stock is insufficient.
      // Eligible single-variant additions must still be grouped so cart quantity increases can
      // roll over into the next matching expiry batch when the primary batch is exhausted.
      if (remaining > 0) return originalFormData;

      const splitFormData = new FormData();
      const properties = [];
      let sellingPlan = null;
      const groupId = `expiry-${product.id}-${currentVariant.price}-${currentVariant.compare_at_price || 0}`;

      for (const [key, value] of originalFormData.entries()) {
        if (key.startsWith('properties[')) {
          properties.push([key, value]);
        } else if (key === 'selling_plan') {
          sellingPlan = value;
        } else if (key !== 'id' && key !== 'quantity' && key !== 'product-id') {
          splitFormData.append(key, value);
        }
      }

      allocations.forEach((allocation, index) => {
        splitFormData.append(`items[${index}][id]`, allocation.id);
        splitFormData.append(`items[${index}][quantity]`, allocation.quantity);
        splitFormData.append(`items[${index}][properties][_expiry_group]`, groupId);
        splitFormData.append(`items[${index}][properties][_expiry_primary]`, Number(allocation.id) === currentVariantId ? 'true' : 'false');
        if (sellingPlan) splitFormData.append(`items[${index}][selling_plan]`, sellingPlan);
        properties.forEach(([key, value]) => {
          const propertyName = key.slice('properties['.length, -1);
          splitFormData.append(`items[${index}][properties][${propertyName}]`, value);
        });
      });

      return splitFormData;
    }
  };

  const mergeRows = () => window.EurusExpiryVariants.mergeCartRows();
  document.addEventListener('DOMContentLoaded', mergeRows);
  document.addEventListener('eurus:cart:items-changed', () => requestAnimationFrame(mergeRows));
  new MutationObserver(() => requestAnimationFrame(mergeRows)).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
